import { useState } from 'react';
import { ErrorDeApi, mandarResultadosConocidos, type Cuestionario } from './api';
import {
  comoSeLee,
  partesParaResultados,
  valoresIniciales,
  type ParteParaResultado,
} from './resultados-conocidos';

/**
 * Poner el resultado que ya se conoce, en vez de responder el test.
 *
 * PARA QUIÉN ES: alguien que ya hizo este mismo test y tiene sus números a
 * mano. Se los pedimos, le armamos su mapa y llega a la misma pantalla final
 * que quien lo respondió aquí.
 *
 * AQUÍ SÍ SE USA UN DESLIZADOR, Y EN EL TEST NO. No es una contradicción, es que
 * son dos cosas distintas. Un ítem del test se responde eligiendo entre seis
 * frases, y ahí un deslizador nace en una posición que parece una respuesta que
 * nadie dio: por eso son botones. Esto no es un ítem: es transcribir un número
 * que la persona ya tiene escrito, quince veces seguidas. Con botones serían
 * noventa toques; arrastrando es un gesto por línea, y el número se ve mientras
 * se mueve.
 *
 * EL SALTO DEL DESLIZADOR NO ES LIBRE. Va de a un paso de los que el resultado
 * puede tomar de verdad —con cinco ítems, de dos en dos décimas—, para que el
 * número que se elige aquí sea exactamente el que va a salir en el mapa.
 *
 * NI UNA PALABRA CLÍNICA, como en el resto de esta web: los nombres de las
 * partes vienen del cuestionario que escribe la psicóloga.
 */

export interface PropiedadesDeResultadosConocidos {
  cuestionario: Cuestionario;
  /** Se llama con el texto de cierre cuando el resultado ya quedó guardado. */
  alTerminar: (mensaje: string | null) => void;
  /** Volver a responder el test normalmente. */
  alVolver: () => void;
  /** La sesión dejó de valer: la pantalla de fallo la pinta el padre. */
  alPerderSesion: () => void;
}

export function ResultadosConocidos({
  cuestionario,
  alTerminar,
  alVolver,
  alPerderSesion,
}: PropiedadesDeResultadosConocidos) {
  const [partes] = useState<ParteParaResultado[]>(() => partesParaResultados(cuestionario));
  const [valores, setValores] = useState<Record<string, number>>(() => valoresIniciales(partes));
  const [enviando, setEnviando] = useState(false);
  const [noSalio, setNoSalio] = useState(false);

  const enviar = async (): Promise<void> => {
    setEnviando(true);
    setNoSalio(false);

    try {
      const { mensaje } = await mandarResultadosConocidos(
        partes.map((parte) => ({
          grupoId: parte.grupoId,
          // El `??` no debería hacer falta —todo control tiene su valor desde el
          // principio—, pero mandar `undefined` haría que el servidor rechazara
          // el lote entero por una parte que en pantalla se veía puesta.
          promedio: valores[parte.grupoId] ?? parte.inicial,
        })),
      );
      alTerminar(mensaje);
    } catch (error) {
      if (error instanceof ErrorDeApi && error.motivo === 'sin_sesion') {
        alPerderSesion();
        return;
      }
      setNoSalio(true);
      setEnviando(false);
    }
  };

  return (
    <main className="hoja">
      <img className="marca" src="/marca/logo-sobre-claro.svg" alt="Ser Prana" />

      <h1>Tus resultados</h1>

      <p className="introduccion">
        Si ya hiciste este test antes y tienes tus resultados a la mano, ponlos aquí y armamos tu
        mapa con ellos. Si prefieres responderlo, puedes volver.
      </p>

      <div className="resultados">
        {partes.map((parte) => {
          const valor = valores[parte.grupoId] ?? parte.inicial;
          const campo = `resultado-${parte.grupoId}`;

          return (
            <div className="resultado" key={parte.grupoId}>
              <div className="resultado__fila">
                <label className="resultado__nombre" htmlFor={campo}>
                  {parte.titulo}
                </label>
                {/*
                 * `output` y no un `span`: un lector de pantalla lo anuncia al
                 * cambiar, así que quien no ve el deslizador oye el número que
                 * está poniendo.
                 */}
                <output className="resultado__valor" htmlFor={campo}>
                  {comoSeLee(valor)}
                </output>
              </div>

              <input
                className="resultado__control"
                id={campo}
                type="range"
                min={parte.minimo}
                max={parte.maximo}
                step={parte.paso}
                value={valor}
                aria-valuetext={comoSeLee(valor)}
                disabled={enviando}
                onChange={(evento) => {
                  const puesto = Number(evento.target.value);
                  setValores((previos) => ({ ...previos, [parte.grupoId]: puesto }));
                }}
              />

              {(parte.extremoBajo ?? parte.extremoAlto) !== null && (
                <div className="resultado__extremos" aria-hidden="true">
                  <span>{parte.extremoBajo}</span>
                  <span>{parte.extremoAlto}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {noSalio && (
        <p className="aviso aviso--problema" role="alert">
          No se pudo guardar. Revisa la conexión y vuelve a intentarlo.
        </p>
      )}

      <div className="botonera">
        <button
          type="button"
          className="boton boton--secundario"
          disabled={enviando}
          onClick={alVolver}
        >
          Atrás
        </button>

        <button
          type="button"
          className="boton"
          disabled={enviando}
          onClick={() => {
            void enviar();
          }}
        >
          {enviando ? 'Guardando…' : 'Ver mi mapa'}
        </button>
      </div>
    </main>
  );
}
